import { Component, inject, signal, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { HeaderComponent } from '../header/header.component';
import { MobileHeaderComponent } from '../mobile-header/mobile-header.component';
import { CreateDispatchDialogComponent } from '../create-dispatch-dialog/create-dispatch-dialog.component';
import { DispatchActionsDrawerComponent } from '../dispatch-actions-drawer/dispatch-actions-drawer.component';
import { AssignmentService, DispatchActionsConfigService } from '../../services';
import { CreateDispatchUiService } from '../../services/create-dispatch-ui.service';
import { printElementInPlace } from '../../services/print.service';
import { DetailPageInfo, VehicleInfo, Assignment } from '../../models';
import { DispatchActionsConfig } from '../../models/dispatch-actions-config.model';
import { filter } from 'rxjs';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-layout',
  imports: [
    SidebarComponent,
    HeaderComponent,
    MobileHeaderComponent,
    ConfirmDialogModule,
    ToastModule,
    CreateDispatchDialogComponent,
    DispatchActionsDrawerComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly assignmentService = inject(AssignmentService);
  private readonly dispatchConfigService = inject(DispatchActionsConfigService);
  protected readonly createDispatchUiService = inject(CreateDispatchUiService);

  readonly detailPageInfo = signal<DetailPageInfo | null>(null);
  readonly currentAssignment = signal<Assignment | null>(null);
  readonly isDetailPage = signal(false);
  readonly assignmentDescription = signal('');

  // Dispatch actions drawer state (shared here for cross-route persistence).
  readonly dispatchActionsVisible = signal(false);
  readonly dispatchActionsConfig = signal<DispatchActionsConfig>({
    appointmentId: '',
    claimNumber: '',
    claimSequence: '',
    timestamps: {},
    presets: {},
  });

  constructor() {
    this.checkRoute();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.checkRoute();
      });

    this.assignmentService.locationUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const info = this.detailPageInfo();
        if (info?.claimNumber) {
          this.loadClaimSequence(
            info.claimNumber,
            info.claimSequence || null,
            info.vendorSequence || null,
          );
        }
      });
  }

  onDispatchCreated(config: DispatchActionsConfig): void {
    this.dispatchActionsConfig.set({ ...config, isNewDispatch: true });
    this.dispatchActionsVisible.set(true);
  }

  private checkRoute(): void {
    // Extract the claim number from the URL when on a detail page.
    const urlSegments = this.router.url.split('/');
    const detailIndex = urlSegments.indexOf('detail');
    const isClaimOverview = urlSegments.includes('claim-overview');
    const isPolicyPage = urlSegments.includes('policy');
    const isLossPage = urlSegments.includes('loss');
    const isPartiesPage = urlSegments.includes('parties');
    const isUnitsPage = urlSegments.includes('units');
    const isNotesPage = urlSegments.includes('notes');
    const isActivityLog = urlSegments.includes('activity-log');
    const isStopStorageLetters = urlSegments.includes('stop-storage-letters');

    if (detailIndex !== -1 && urlSegments[detailIndex + 1]) {
      // Parse the composite ID (claimNumber-seq-vendSeq) to match a specific assignment.
      const compositeId = urlSegments[detailIndex + 1];
      const parts = compositeId.split('-');
      const claimNum = parts[0];
      const seq = parts.length >= 2 ? parts[1] : null;
      const vendSeq = parts.length >= 3 ? parts[2] : null;
      this.isDetailPage.set(true);
      this.loadClaimSequence(claimNum, seq, vendSeq);
    } else if (isStopStorageLetters) {
      // Stop-storage-letters page with a compositeId.
      this.isDetailPage.set(true);
      const pageIndex = urlSegments.indexOf('stop-storage-letters');
      if (urlSegments[pageIndex + 1]) {
        const compositeId = urlSegments[pageIndex + 1];
        const parts = compositeId.split('-');
        const claimNum = parts[0];
        const seq = parts.length >= 2 ? parts[1] : null;
        const vendSeq = parts.length >= 3 ? parts[2] : null;
        this.loadClaimSequence(claimNum, seq, vendSeq);
      }
    } else if (
      isClaimOverview ||
      isPolicyPage ||
      isLossPage ||
      isPartiesPage ||
      isUnitsPage ||
      isNotesPage ||
      isActivityLog
    ) {
      // Claim overview, policy, loss, parties, units, notes, and activity log pages.
      this.isDetailPage.set(true);
      const pageIndex = isClaimOverview
        ? urlSegments.indexOf('claim-overview')
        : isPolicyPage
          ? urlSegments.indexOf('policy')
          : isLossPage
            ? urlSegments.indexOf('loss')
            : isPartiesPage
              ? urlSegments.indexOf('parties')
              : isUnitsPage
                ? urlSegments.indexOf('units')
                : isNotesPage
                  ? urlSegments.indexOf('notes')
                  : urlSegments.indexOf('activity-log');
      if (urlSegments[pageIndex + 1]) {
        const claimNum = urlSegments[pageIndex + 1];
        const seq = urlSegments[pageIndex + 2] || '';
        // Set minimal detailPageInfo from URL params so sidebar navigation works immediately.
        if (!this.detailPageInfo()?.claimNumber) {
          this.detailPageInfo.set({
            claimNumber: claimNum,
            claimSequence: seq,
            vendorSequence: '',
            appointmentId: '',
            dispatchId: '',
            userId: '',
          } as DetailPageInfo);
        }
        // Load the full assignment so sidebar Payments has the dispId (id6).
        if (!this.detailPageInfo()?.dispatchId) {
          this.loadClaimSequence(claimNum, seq || null);
        }
      }
    } else {
      this.detailPageInfo.set(null);
      this.currentAssignment.set(null);
      this.isDetailPage.set(false);
    }
  }

  /** Loads assignment data for the sidebar, matching seq/vendSeq when provided. */
  private loadClaimSequence(
    claimNumber: string,
    seq?: string | null,
    vendSeq?: string | null,
  ): void {
    this.assignmentService
      .getAssignmentsByClaimNumberViaGatewayPost(claimNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (assignments) => {
          if (assignments.length > 0) {
            // Find the specific assignment by seq and vendSeq.
            let assignment = null;

            if (seq && vendSeq) {
              assignment =
                assignments.find(
                  (a) =>
                    (a.seq === seq || a.claimSequence === seq) &&
                    (a.appointmentID?.toString() === vendSeq || a.apptId === vendSeq),
                ) ??
                assignments.find((a) => a.seq === seq || a.claimSequence === seq) ??
                assignments[0];
            } else if (seq) {
              // Match by seq only (Policy, Parties, Units, Notes pages).
              assignment =
                assignments.find((a) => a.seq === seq || a.claimSequence === seq) ?? assignments[0];
            } else {
              // No seq provided — use the first assignment (backward compatibility).
              assignment = assignments[0];
            }

            const vehicle: VehicleInfo = {
              vin: assignment.vehicleVin || '',
              year: assignment.vehicleYear,
              make: assignment.vehicleMake,
              model: assignment.vehicleModel,
            };

            // Store assignment for dispatch actions (sidebar uses it directly).
            this.currentAssignment.set(assignment);

            // Prefer the URL param seq to keep breadcrumb/navigation consistent.
            this.detailPageInfo.set({
              claimNumber: claimNumber,
              claimSequence: seq || assignment.seq || assignment.claimSequence || '',
              vendorSequence: vendSeq || assignment.vendSeq || assignment.vendorSequence || '',
              appointmentId: assignment.appointmentID?.toString() || '',
              // id6 is the check-writer dispId (not assignment.dispatchId).
              dispatchId: assignment.id6 || '',
              userId: assignment.id3 || '', // Numeric user ID
              vehicle: vehicle,
              appraiser: assignment.id4Txt || '',
              appraiserGroup: assignment.id13Txt || '', // Appraiser group code for DTD detection
              location: this.dispatchConfigService.buildLocationString(assignment),
              contactPhone: assignment.contactPhoneDay,
              // Action timestamps
              acknowledgeDone: assignment.autoAcknowledgeDone,
              sendEmailDone: assignment.sendEmailDone,
              sendEmailWithCalendarDone: assignment.sendEmailWithCalendarDone,
              sendTextDone: assignment.sendTextDone,
              sendNotificationDone: assignment.sendNotificationDone,
              transmitAutoVendorDone: assignment.placeholder2Done,
              transmitHomeVendorDone: assignment.placeholder3Done,
              transmitCarPicsDone: assignment.rockPicsDone,
            });

            // Store description for printing
            if (assignment.description) {
              this.assignmentDescription.set(assignment.description);
            }
          }
        },
        error: (error) => {
          console.error('Error loading claim sequence:', error);
        },
      });
  }

  /** Prints the assignment description in place without navigating away. */
  handlePrintAssignmentSheet(): void {
    if (!this.assignmentDescription()) return;
    setTimeout(() => printElementInPlace('printCanvas'));
  }
}
